import React, { useCallback, useEffect, useState } from "react";
import { Search, Mail, Phone, Download, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { AdminCard, PageTitle, StatusPill } from "@/pages/admin/AdminLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { centsToEuro } from "@/lib/pricing";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const cls = "w-full h-11 px-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";

export default function AdminCustomers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState({ name: "", phone: "", country: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/customers", { params: q.trim() ? { q: q.trim() } : {} }).then((r) => setList(r.data)).finally(() => setLoading(false));
  }, [q]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const open = async (c) => {
    try {
      const r = await adminApi.get(`/admin/customers/${c.id}`);
      setSel(r.data);
      setEdit({ name: r.data.name || "", phone: r.data.phone || "", country: r.data.country || "", notes: r.data.notes || "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await adminApi.patch(`/admin/customers/${sel.id}`, edit);
      setSel({ ...sel, ...r.data });
      toast.success("Cliente aggiornato");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const exportJson = async () => {
    try {
      const r = await adminApi.get(`/admin/customers/${sel.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ams-cliente-${sel.email}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Dati esportati (GDPR art. 15)");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const erase = async () => {
    try {
      const r = await adminApi.delete(`/admin/customers/${sel.id}`);
      toast.success(`Dati anonimizzati: ${r.data.bookings_anonymised} prenotazioni, ${r.data.inquiries_anonymised} richieste`);
      setConfirmErase(false);
      setSel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-customers">
      <PageTitle title="Clienti" sub="Anagrafica creata automaticamente da prenotazioni e richieste. Strumenti GDPR: esportazione e cancellazione." />
      <AdminCard className="p-4 mb-5">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ocean/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email o telefono" className={`${cls} pl-10`} data-testid="admin-customers-search" />
        </div>
      </AdminCard>
      <AdminCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-ivory/70 text-[11px] uppercase tracking-[0.15em] text-ocean/55">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Cliente</th>
                <th className="text-left px-5 py-3 font-medium">Telefono</th>
                <th className="text-right px-5 py-3 font-medium">Prenotazioni</th>
                <th className="text-right px-5 py-3 font-medium">Richieste</th>
                <th className="text-left px-5 py-3 font-medium">Privacy</th>
                <th className="text-left px-5 py-3 font-medium">Dal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand/60">
              {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-ocean/45">Caricamento...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-ocean/45" data-testid="admin-customers-empty">Nessun cliente trovato.</td></tr>}
              {!loading && list.map((c) => (
                <tr key={c.id} onClick={() => open(c)} className="hover:bg-sand/30 cursor-pointer" data-testid={`admin-customer-row-${c.id}`}>
                  <td className="px-5 py-4"><p className="text-ocean font-medium">{c.name || "—"}</p><p className="text-xs text-ocean/50">{c.email}</p></td>
                  <td className="px-5 py-4 text-ocean/75 whitespace-nowrap">{c.phone || "—"}</td>
                  <td className="px-5 py-4 text-right">{c.bookings_count}</td>
                  <td className="px-5 py-4 text-right">{c.inquiries_count}</td>
                  <td className="px-5 py-4 text-xs text-ocean/60">{c.consents?.privacy_at ? "Consenso " + fmt(c.consents.privacy_at) : "n.d."}</td>
                  <td className="px-5 py-4 text-xs text-ocean/60 whitespace-nowrap">{fmt(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto" data-testid="admin-customer-sheet">
          {sel && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl text-ocean">{sel.name || sel.email}</SheetTitle>
                <SheetDescription>Cliente dal {fmt(sel.created_at)} · lingua {(sel.lang || "en").toUpperCase()}</SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-2 text-sm">
                <a href={`mailto:${sel.email}`} className="flex items-center gap-2 text-ocean/70 hover:text-sea"><Mail size={14} />{sel.email}</a>
                {sel.phone && <a href={`tel:${sel.phone}`} className="flex items-center gap-2 text-ocean/70 hover:text-sea"><Phone size={14} />{sel.phone}</a>}
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[["name", "Nome"], ["phone", "Telefono"], ["country", "Paese"]].map(([k, l]) => (
                  <div key={k} className={k === "name" ? "sm:col-span-2" : ""}>
                    <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{l}</label>
                    <input className={cls} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`admin-customer-${k}`} />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Note interne</label>
                  <textarea rows={2} className="w-full px-4 py-3 rounded-xl border border-sand text-sm focus:outline-none focus:border-sea resize-none" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} data-testid="admin-customer-notes" />
                </div>
              </div>
              <button onClick={save} disabled={busy} className="mt-3 px-5 py-2.5 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-50 btn-lift" data-testid="admin-customer-save">Salva</button>

              <div className="mt-8">
                <p className="text-[11px] tracking-[0.2em] uppercase text-ocean/55 mb-2">Prenotazioni ({sel.bookings?.length || 0})</p>
                {(sel.bookings || []).length === 0 ? <p className="text-xs text-ocean/45">Nessuna.</p> : (
                  <ul className="space-y-2 text-sm" data-testid="admin-customer-bookings">
                    {sel.bookings.map((b) => (
                      <li key={b.id} className="flex items-center justify-between gap-3 bg-ivory rounded-xl px-4 py-2.5">
                        <div className="min-w-0"><p className="text-ocean truncate">{b.tour_title}</p><p className="text-xs text-ocean/50">{b.ref} · {b.date} · {b.guests} pax · {b.total_cents !== undefined ? centsToEuro(b.total_cents) : `€${b.total}`}</p></div>
                        <StatusPill status={b.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mt-6">
                <p className="text-[11px] tracking-[0.2em] uppercase text-ocean/55 mb-2">Richieste ({sel.inquiries?.length || 0})</p>
                {(sel.inquiries || []).length === 0 ? <p className="text-xs text-ocean/45">Nessuna.</p> : (
                  <ul className="space-y-2 text-xs text-ocean/70">
                    {sel.inquiries.map((i) => <li key={i.id} className="bg-ivory rounded-xl px-4 py-2.5">{fmt(i.created_at)} · {i.kind === "dates" ? "Date / preventivo" : "Contatto"} {i.message ? `· ${i.message.slice(0, 90)}` : ""}</li>)}
                  </ul>
                )}
              </div>

              <div className="mt-8 border-t border-sand pt-5">
                <p className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-ocean/55 mb-3"><ShieldAlert size={14} />GDPR</p>
                <div className="flex flex-wrap gap-3">
                  <button onClick={exportJson} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-sand text-sm text-ocean" data-testid="admin-customer-export"><Download size={14} />Esporta dati (JSON)</button>
                  <button onClick={() => setConfirmErase(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-rose-200 text-sm text-rose-700" data-testid="admin-customer-erase"><Trash2 size={14} />Cancella dati personali</button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmErase} onOpenChange={setConfirmErase}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancellare i dati personali?</AlertDialogTitle>
            <AlertDialogDescription>Nome, email, telefono e note vengono anonimizzati in prenotazioni e richieste (che restano per la contabilità) e il profilo viene eliminato. L'operazione viene registrata nel registro attività e non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-erase-cancel">Indietro</AlertDialogCancel>
            <AlertDialogAction onClick={erase} className="bg-rose-700 hover:bg-rose-800" data-testid="admin-erase-confirm">Anonimizza</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
